// Global Instructions Rule Applied!
//
// Configuration → Interaction Tracking services. Same conventions as
// emailCategoriesConfigService: the { data: {...} } request envelope, and
// `message` read before `error` so the RPC's own refusals surface readably.
import { API_ROUTES, buildFullUrl, getBackendBaseUrl } from '../../../core/utils/endpoints.ts'
import {
  InteractionTrackingConfigAdminData,
  MailboxCategoryLabel,
  TrackedMailboxRow,
  validateMailboxCategoryLabel,
  validateTrackedMailboxRow
} from '../../../state_manager/data_models/interaction_tracking_config_model.ts'

const BACKEND_BASE_URL = getBackendBaseUrl()

function authHeaders(accessToken: string | null): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`
  return headers
}

async function readJson(response: Response): Promise<any> {
  const body = await response.json().catch(() => ({}))
  if (!response.ok) {
    if (response.status === 401) throw new Error('Authentication failed - please log in again')
    throw new Error(body.message || body.error || `Request failed (${response.status})`)
  }
  return body
}

export const fetchInteractionTrackingConfig = async (
  accessToken: string | null
): Promise<InteractionTrackingConfigAdminData> => {
  const response = await fetch(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.INTERACTION_TRACKING_CONFIG),
    { method: API_ROUTES.CONFIG.INTERACTION_TRACKING_CONFIG.method, headers: authHeaders(accessToken) }
  )
  const body = await readJson(response)
  const data = body.data || {}
  const mailboxes = (data.mailboxes || []) as TrackedMailboxRow[]
  const labelOverrides = (data.label_overrides || []) as MailboxCategoryLabel[]
  validateTrackedMailboxRow(mailboxes, { isArray: true })
  validateMailboxCategoryLabel(labelOverrides, { isArray: true })
  return { mailboxes, labelOverrides }
}

export interface TrackedMailboxFields {
  display_name?: string | null
  email_tracking_enabled?: boolean
  event_tracking_enabled?: boolean
  categorization_enabled?: boolean
  /** Send to Outlook may create agent drafts in this mailbox (Phase 6a).
   *  Requires mail tracking — the RPC refuses otherwise. */
  outlook_drafts_enabled?: boolean
  /** Email campaigns on the mailbox channel may be sent from this mailbox. */
  campaign_send_enabled?: boolean
  notes?: string | null
}

/** Returns a warning when the switch was saved but the immediate Graph
 *  subscription teardown could not be reached — the change stands either way. */
export const updateTrackedMailbox = async (
  accessToken: string | null,
  mailboxId: string,
  fields: TrackedMailboxFields
): Promise<string | null> => {
  const response = await fetch(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.INTERACTION_TRACKING_MAILBOX_UPDATE, { mailboxId }),
    {
      method: API_ROUTES.CONFIG.INTERACTION_TRACKING_MAILBOX_UPDATE.method,
      headers: authHeaders(accessToken),
      body: JSON.stringify({ data: fields })
    }
  )
  const body = await readJson(response)
  return body?.data?.prog__teardown_warning ?? null
}

export const addTrackedMailbox = async (
  accessToken: string | null,
  mailbox: string,
  displayName?: string
): Promise<void> => {
  const response = await fetch(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.INTERACTION_TRACKING_MAILBOX_ADD),
    {
      method: API_ROUTES.CONFIG.INTERACTION_TRACKING_MAILBOX_ADD.method,
      headers: authHeaders(accessToken),
      body: JSON.stringify({ data: { mailbox, display_name: displayName } })
    }
  )
  await readJson(response)
}

export const removeTrackedMailbox = async (
  accessToken: string | null,
  mailboxId: string
): Promise<void> => {
  const response = await fetch(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.INTERACTION_TRACKING_MAILBOX_REMOVE, { mailboxId }),
    {
      method: API_ROUTES.CONFIG.INTERACTION_TRACKING_MAILBOX_REMOVE.method,
      headers: authHeaders(accessToken)
    }
  )
  await readJson(response)
}

/** Per (mailbox, category) Outlook label/colour override. Passing both blank
 *  clears the override entirely. */
export const setMailboxCategoryLabel = async (
  accessToken: string | null,
  mailboxId: string,
  categoryId: string,
  label: string | null,
  color: string | null
): Promise<void> => {
  const response = await fetch(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.INTERACTION_TRACKING_MAILBOX_LABEL, { mailboxId }),
    {
      method: API_ROUTES.CONFIG.INTERACTION_TRACKING_MAILBOX_LABEL.method,
      headers: authHeaders(accessToken),
      body: JSON.stringify({ data: { category_id: categoryId, label, color } })
    }
  )
  await readJson(response)
}
